import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { CenteredSpinner } from "../CenteredSpinner";
import {
  useActionSets,
  useAutoTransactionRules,
  useBulkUpdateTransactions,
  useConfig,
  useTransactions,
} from "../../api/queries";
import { useTransactionPaging } from "../hooks/useTransactionPaging";
import { zeroMoney } from "../../logic/money";
import type { StoredTransaction } from "../../logic/derivedAccount";
import { buildAccountNumberIndex } from "../../logic/counterparty";
import { useRegistry } from "../../context/RegistryContext";
import type {
  AutoTransactionRuleDto,
  EditActionDto,
  GetTransactionsQueryDto,
  Money,
  TransactionId,
  TransactionListDto,
} from "../../api/types";
import { SplitTransactionDialog } from "../SplitTransactionDialog";
import { TransactionDetailsPage } from "../../pages/TransactionDetailsPage";
import { AutoTransactionForm } from "../../pages/Settings/AutoTransactionForm";
import { PromptRuleDialog } from "../PromptRuleDialog";
import { collectPrompts } from "../PromptRuleDialog/prompts";
import { AppliedRulesMenu, useAppliedRulesMenu } from "../AppliedRulesMenu";
import { TransactionListPagination } from "./TransactionListPagination";
import { TransactionListMobile } from "./TransactionListMobile";
import { TransactionRow } from "./TransactionRow";
import {
  EditingPickerModal,
  type EditingCell,
  type PickerField,
} from "./EditingPickerModal";
import { useEditSession, useEditedCount } from "./editSession";

export interface TransactionListProps {
  filters: GetTransactionsQueryDto;
}

/// Column set for the desktop table; the cells live in `TransactionRow`.
/// Every column but the flexible one is sized to its content, so the table
/// has one place to put its spare width and one place to lose it: the
/// description. Ten content-sized columns would otherwise share the slack
/// and, at the container's width, push the actions off the right edge.
const COLUMNS: {
  label: string;
  align?: "right" | "center";
  flexible?: boolean;
}[] = [
  { label: "Date Paid" },
  { label: "Source" },
  { label: "Description", flexible: true },
  { label: "Expense Type" },
  { label: "Account" },
  { label: "Group" },
  { label: "Categories" },
  { label: "Foreign Amount", align: "right" },
  { label: "Amount", align: "right" },
  { label: "Actions", align: "center" },
];

export const TransactionList = ({ filters }: TransactionListProps) => {
  const queryClient = useQueryClient();

  const { pageFilters, page, hasPrevious, goToNextPage, goToPreviousPage } =
    useTransactionPaging(filters);
  const {
    data: transactionPage,
    isLoading,
    error,
  } = useTransactions(pageFilters);
  const transactions = transactionPage?.items;
  const { data: rules } = useAutoTransactionRules();
  const { data: config } = useConfig();
  const actionSets = useActionSets();
  const updateTransactions = useBulkUpdateTransactions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const appliedRules = useAppliedRulesMenu();
  // Hidden accounts included: a transfer to one still names it.
  const { sources } = useRegistry().withHidden;

  // One index for the whole table: the rows are memoized, so building it per
  // row would hand each one a fresh Map and defeat that.
  const accountNumberIndex = useMemo(
    () => buildAccountNumberIndex(sources),
    [sources],
  );

  const session = useEditSession();
  const editedCount = useEditedCount(session);

  const [editingCell, setEditingCell] = useState<{
    id: TransactionId;
    field: PickerField;
  } | null>(null);
  const [pendingPromptRule, setPendingPromptRule] = useState<{
    rule: AutoTransactionRuleDto;
    txId: TransactionId;
  } | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] =
    useState<TransactionId | null>(null);
  const [ruleCreationTransaction, setRuleCreationTransaction] =
    useState<StoredTransaction | null>(null);
  const [splitDialog, setSplitDialog] = useState<{
    open: boolean;
    transactionId: TransactionId | null;
    originalAmount: Money;
  }>({ open: false, transactionId: null, originalAmount: zeroMoney() });
  const [suggestion, setSuggestion] = useState<{
    anchorEl: HTMLElement;
    txId: TransactionId;
  } | null>(null);

  const matchingRulesMap = useMemo(() => {
    if (!transactions || !rules) return {};
    const byId = new Map(rules.map((r) => [r.id, r] as const));
    const map: Record<TransactionId, AutoTransactionRuleDto[]> = {};
    for (const tx of transactions) {
      const matches = tx.matchingRules
        .map((info) => byId.get(info.id))
        .filter((r): r is AutoTransactionRuleDto => Boolean(r));
      if (matches.length > 0) {
        map[tx.id] = matches;
      }
    }
    return map;
  }, [transactions, rules]);

  // Row callbacks. All stable (or session-stable): the rows are memoized, so
  // anything passed down must not change identity on container re-renders.
  const onCommitDescription = useCallback(
    (tx: TransactionListDto, value: string) =>
      session.commitFieldChange(
        tx,
        { field: "description", value },
        { description: value },
      ),
    [session],
  );
  const onOpenPicker = useCallback(
    (id: TransactionId, field: PickerField) => setEditingCell({ id, field }),
    [],
  );
  const onViewDetails = useCallback(
    (id: TransactionId) => setSelectedTransactionId(id),
    [],
  );
  const onCreateRule = useCallback(
    (tx: TransactionListDto) => setRuleCreationTransaction(tx),
    [],
  );
  const onSplit = useCallback(
    (tx: TransactionListDto) =>
      setSplitDialog({
        open: true,
        transactionId: tx.id,
        originalAmount: tx.amount,
      }),
    [],
  );
  const onOpenSuggestions = useCallback(
    (event: React.MouseEvent<HTMLElement>, txId: TransactionId) =>
      setSuggestion({ anchorEl: event.currentTarget, txId }),
    [],
  );

  const closeEditingCell = useCallback(() => setEditingCell(null), []);
  const closeSuggestions = () => setSuggestion(null);

  const handleApplyRule = (
    rule: AutoTransactionRuleDto,
    txId: TransactionId,
  ) => {
    // Expanded, so a prompt inside a cited action set opens the dialog too —
    // and the same call the dialog itself makes, so the two can't disagree.
    if (collectPrompts(rule, actionSets.data).length > 0) {
      setPendingPromptRule({ rule, txId });
      return;
    }
    // Virtual (invoice) rules aren't stored, so the server can't resolve
    // `applyAutoTx(id)`. Apply their concrete actions directly instead.
    session.appendActions(
      txId,
      rule.editable ? [{ field: "applyAutoTx", value: rule.id }] : rule.actions,
    );
  };

  const confirmPromptRule = (actions: EditActionDto[]) => {
    if (!pendingPromptRule) return;
    session.appendActions(pendingPromptRule.txId, actions);
    setPendingPromptRule(null);
  };

  const handleSave = () => {
    const updates = session.collectUpdates();
    if (updates.length === 0) return;
    updateTransactions.mutate(updates, { onSuccess: () => session.clear() });
  };

  const handleCancel = () => {
    session.clear();
    setEditingCell(null);
  };

  const handleSplitConfirm = (amount: Money) => {
    if (!splitDialog.transactionId) return;
    const id = splitDialog.transactionId;
    updateTransactions.mutate(
      [{ id, actions: [{ field: "split", value: amount }] }],
      // The split rewrites the row server-side; local pending edits for it
      // would apply to a row that no longer exists in that shape.
      { onSuccess: () => session.clearRow(id) },
    );
    setSplitDialog((prev) => ({ ...prev, open: false }));
  };

  const handleCloseDetails = () => setSelectedTransactionId(null);
  const handleCloseRuleCreation = () => setRuleCreationTransaction(null);
  const handleRuleCreated = () => {
    setRuleCreationTransaction(null);
    queryClient.invalidateQueries({ queryKey: ["autoTransactionRules"] });
    queryClient.invalidateQueries({ queryKey: ["transactions"] });
  };
  const handleCreateRuleFromDetails = (tx: StoredTransaction) => {
    // Close the details modal to focus on rule creation.
    setSelectedTransactionId(null);
    setRuleCreationTransaction(tx);
  };

  if (isLoading) {
    return <CenteredSpinner padding={2} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading transactions: {error.message}
      </Alert>
    );
  }

  const currencyFormat = config?.currencyFormat ?? "Nok";
  const promptTransaction = pendingPromptRule
    ? (transactions?.find((t) => t.id === pendingPromptRule.txId) ?? null)
    : null;
  const editingTarget: EditingCell | null = (() => {
    if (!editingCell) return null;
    const tx = transactions?.find((t) => t.id === editingCell.id);
    return tx ? { transaction: tx, field: editingCell.field } : null;
  })();

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
      >
        <Typography variant="h6">Transactions</Typography>
        {editedCount > 0 && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={updateTransactions.isPending}
            >
              {updateTransactions.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </Stack>
        )}
      </Stack>
      {updateTransactions.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {updateTransactions.error.message}
        </Alert>
      )}
      {isMobile ? (
        <TransactionListMobile
          transactions={transactions || []}
          session={session}
          matchingRulesMap={matchingRulesMap}
          currencyFormat={currencyFormat}
          onViewDetails={onViewDetails}
          onCreateRule={onCreateRule}
          onOpenSuggestions={onOpenSuggestions}
          onOpenAppliedRules={appliedRules.openAppliedRules}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table
            // Ten columns at MUI's default 16px cell padding spend 320px on
            // gutters alone; the cell wrappers add their own, so the gutters
            // still read as gutters at half that.
            sx={{ minWidth: 650, "& .MuiTableCell-root": { px: 1 } }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map(({ label, align, flexible }) => (
                  <TableCell
                    key={label}
                    align={align}
                    // `width: 1%` is the table idiom for "as narrow as the
                    // content allows": the column keeps its content width
                    // and the flexible column absorbs whatever is left.
                    sx={
                      flexible
                        ? undefined
                        : { whiteSpace: "nowrap", width: "1%" }
                    }
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions?.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  session={session}
                  matchingRules={matchingRulesMap[transaction.id]}
                  currencyFormat={currencyFormat}
                  accountNumberIndex={accountNumberIndex}
                  onCommitDescription={onCommitDescription}
                  onOpenPicker={onOpenPicker}
                  onViewDetails={onViewDetails}
                  onCreateRule={onCreateRule}
                  onSplit={onSplit}
                  onOpenSuggestions={onOpenSuggestions}
                  onOpenAppliedRules={appliedRules.openAppliedRules}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TransactionListPagination
        page={page}
        hasPrevious={hasPrevious}
        hasNext={transactionPage?.hasMore ?? false}
        rowCount={transactions?.length ?? 0}
        onPrevious={goToPreviousPage}
        onNext={() => goToNextPage(transactionPage?.nextCursor ?? null)}
      />
      <Menu
        anchorEl={suggestion?.anchorEl ?? null}
        open={suggestion !== null}
        onClose={closeSuggestions}
      >
        {suggestion &&
          matchingRulesMap[suggestion.txId]?.map((rule) => (
            <MenuItem
              key={rule.id}
              onClick={() => {
                handleApplyRule(rule, suggestion.txId);
                closeSuggestions();
              }}
            >
              {rule.name}
            </MenuItem>
          ))}
      </Menu>
      <AppliedRulesMenu controller={appliedRules} />
      <SplitTransactionDialog
        open={splitDialog.open}
        onClose={() => setSplitDialog((prev) => ({ ...prev, open: false }))}
        onSplit={handleSplitConfirm}
        originalAmount={splitDialog.originalAmount}
      />
      <PromptRuleDialog
        open={pendingPromptRule !== null}
        rule={pendingPromptRule?.rule ?? null}
        transaction={promptTransaction}
        onCancel={() => setPendingPromptRule(null)}
        onConfirm={confirmPromptRule}
      />
      <Dialog
        open={!!selectedTransactionId}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedTransactionId && (
            <TransactionDetailsPage
              transactionId={selectedTransactionId}
              onClose={handleCloseDetails}
              onCreateRule={handleCreateRuleFromDetails}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!ruleCreationTransaction}
        onClose={handleCloseRuleCreation}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {ruleCreationTransaction && (
            <AutoTransactionForm
              initialTransaction={ruleCreationTransaction}
              onClose={handleCloseRuleCreation}
              onSuccess={handleRuleCreated}
            />
          )}
        </DialogContent>
      </Dialog>
      {editingTarget && (
        <EditingPickerModal
          editing={editingTarget}
          session={session}
          onClose={closeEditingCell}
          onChange={session.commitFieldChange}
        />
      )}
    </Box>
  );
};
